import React from 'react';
import { Divider, Field, Input, mergeClasses, Select } from '@fluentui/react-components';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../context';
import { getValidationState } from './components/formValidation';
import { PageReferenceActivity } from '@gkn-ili/views/dist/OrderDocumentation';
import { AppEditDialogActions } from './components/dialogActions';
import {
  AppEditContentFormFields,
  AppEditContentGetDefaultValues,
  AppEditContentGetSaveData,
} from './components/editContent';
import { View3DCameraState } from '../../context/context3D';
import { useLargeModalStyles } from './components/commonStyles';
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  AppDialogHeader,
  AppDialogSurface,
  DialogOptions,
} from './components/baseComponents';
import AppView3DConfig from './components/view3DConfig';
import { findKeyBefore } from '../../utils/arrayHelpers';
import AppActivityConfig, {
  AppActivityConfigFormFields,
  AppActivityConfigGetDefaultValues,
  AppActivityConfigGetSaveData,
} from './components/activityConfig';
import { refIsEqual } from '../../context/context3D/model3dState';
import AppModelTreeGrid, { defaultFieldGenerators } from './components/modelTreeGrid';

type FormState = { name: string; description: string; previousActivity: string } & AppEditContentFormFields &
  AppActivityConfigFormFields;

export default function AppEditActivityModal(props: {
  activityReference: PageReferenceActivity;
  cameraView?: View3DCameraState;
  dialogOptions: DialogOptions;
}): JSX.Element {
  const styles = useLargeModalStyles();

  const appState = useAppState();
  const appActions = useAppActions();
  const parentActivityGroup =
    appState.orderView.routing[props.activityReference.routingItemKey].steps[props.activityReference.stepKey]
      .activityGroups[props.activityReference.activityGroupKey];
  const activity = parentActivityGroup.activities[props.activityReference.activityKey];

  const formHandlers = useForm<FormState>({
    defaultValues: {
      name: activity.name,
      description: activity.description,
      previousActivity: findKeyBefore(Object.keys(parentActivityGroup.activities), props.activityReference.activityKey),
      ...AppEditContentGetDefaultValues(activity),
      ...AppActivityConfigGetDefaultValues(activity),
    },
  });
  const valueName = formHandlers.watch('name');
  const valueDescription = formHandlers.watch('description');
  const valueContentType = formHandlers.watch('contentType');
  const valueActivityVisuals = formHandlers.watch('activity.subjects');
  const modifiers = formHandlers.watch('model.model.configuration.modifiers');

  function onHideShow(reference: string[], visible: boolean) {
    const allData = formHandlers.getValues();
    const actualModifiers = allData.model.model.configuration.modifiers;
    formHandlers.setValue('model.model.configuration.modifiers', [...actualModifiers, { reference, visible }]);
  }
  const onSelect = (ref: string[], newActiveState: boolean) => {
    const result = valueActivityVisuals.filter((a) => !refIsEqual(a.ref, ref));
    if (newActiveState) {
      result.push({ ref, showPartNumber: true, offsetPosition: '0;0;0' });
    }
    formHandlers.setValue('activity.subjects', result);
  };
  const onToggleAnnotation = (ref: string[], newAnnotationState: boolean) => {
    const target = valueActivityVisuals.find((k) => refIsEqual(k.ref, ref));
    if (!target) {
      return;
    }
    // Remove so we can add a duplicate
    const result = valueActivityVisuals.filter((k) => !refIsEqual(k.ref, ref));
    const newTarget = { ...target, showPartNumber: newAnnotationState };
    result.push(newTarget);
    formHandlers.setValue('activity.subjects', result);
  };
  const onUpdateOffsetPosition = (ref: string[], newOffsetPosition: string) => {
    const target = valueActivityVisuals.find((k) => refIsEqual(k.ref, ref));
    if (!target) {
      return;
    }
    // Remove so we can add a duplicate
    const result = valueActivityVisuals.filter((k) => !refIsEqual(k.ref, ref));
    const newTarget = { ...target, offsetPosition: newOffsetPosition };
    result.push(newTarget);
    formHandlers.setValue('activity.subjects', result);
  };
  const onSubmit = formHandlers.handleSubmit(async (data) => {
    const content = AppEditContentGetSaveData(data, activity, props.cameraView);
    const activityData = AppActivityConfigGetSaveData(data);
    await appActions.editOrder.editActivity(
      props.activityReference,
      {
        name: data.name,
        description: data.description,
        content,
        ...activityData,
      },
      data.previousActivity,
    );
    props.dialogOptions.onCloseDialog();
  });
  const modelContent = (
    <>
      <div className={mergeClasses(styles.groupLH, styles.groupRow3)}>
        <AppActivityConfig />
      </div>
      <div className={mergeClasses(styles.groupRH, styles.groupRow3)}>
        <AppView3DConfig />
      </div>

      <div className={mergeClasses(styles.groupSpanCol, styles.groupRow4)}>
        <Divider>3D Data</Divider>
        <AppModelTreeGrid
          fieldGenerators={[
            defaultFieldGenerators.hideShow(
              modifiers.map((rule) => ({ ...rule, inheritanceLevel: 0 })),
              onHideShow,
            ),
            defaultFieldGenerators.toggleActive(valueActivityVisuals, onSelect),
            defaultFieldGenerators.toggleAnnotation(valueActivityVisuals, onToggleAnnotation),
            defaultFieldGenerators.editOffsetPosition(valueActivityVisuals, onUpdateOffsetPosition),
          ]}
        />
      </div>
    </>
  );

  return (
    <AppDialogSurface dialogOptions={props.dialogOptions} className={styles.root}>
      <FormProvider {...formHandlers}>
        <form>
          <AppDialogBody dialogOptions={props.dialogOptions}>
            <AppDialogHeader dialogOptions={props.dialogOptions}>Edit Activity</AppDialogHeader>
            <AppDialogContent dialogOptions={props.dialogOptions} className={styles.twoColumnContainer}>
              <Divider className={styles.groupBasicHeader}>Basic</Divider>
              <div className={styles.groupBasicLH}>
                <Field
                  orientation="horizontal"
                  label="Activity name"
                  required
                  {...getValidationState('name', formHandlers.formState.errors)}
                >
                  <Input
                    {...formHandlers.register('name', { required: true })}
                    name="name"
                    value={valueName}
                    className={styles.field}
                  />
                </Field>
                <Field
                  orientation="horizontal"
                  label="Activity description"
                  {...getValidationState('description', formHandlers.formState.errors)}
                >
                  <Input {...formHandlers.register('description')} value={valueDescription} className={styles.field} />
                </Field>
              </div>
              <div className={styles.groupBasicRH}>
                <Field
                  orientation="horizontal"
                  label="Content type"
                  required
                  {...getValidationState('contentType', formHandlers.formState.errors)}
                >
                  <Select {...formHandlers.register('contentType')}>
                    <option value="text">Text</option>
                    <option value="model">3D Model</option>
                  </Select>
                </Field>
                <Field
                  orientation="horizontal"
                  label="Comes after activity"
                  required
                  {...getValidationState('previousActivity', formHandlers.formState.errors)}
                >
                  <Select {...formHandlers.register('previousActivity', { required: true })}>
                    <option value="none">As first</option>
                    {Object.entries(parentActivityGroup.activities)
                      .filter(([key]) => key !== props.activityReference.activityKey)
                      .map(([key, value]) => (
                        <option key={key} value={key}>
                          {value.name}
                        </option>
                      ))}
                  </Select>
                </Field>
              </div>
              {valueContentType === 'model' ? modelContent : <span>Not implemented</span>}
            </AppDialogContent>
            <AppDialogFooter dialogOptions={props.dialogOptions}>
              <AppEditDialogActions
                onCancel={props.dialogOptions.onCloseDialog}
                submitDisabled={formHandlers.formState.isSubmitting}
                onSubmit={onSubmit}
              />
            </AppDialogFooter>
          </AppDialogBody>
        </form>
      </FormProvider>
    </AppDialogSurface>
  );
}
